import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import * as PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import cuid from 'cuid';
import get from 'lodash/get';
import cn from 'classnames';

import Message from './Message';
import { OTContext } from '../../../../redux/opentok/context';
import {
  makeNotificationBuilder,
  subscribeToNotification,
} from '../../../../redux/opentok/helpers/notifications';
import styles from './styles.styl';

const NOTIFICATION_TIME = 5e3;

function VideoNotifications({
  intl: { formatMessage },
  fullScreenView = false,
}) {
  const [notifications, setNotifications] = useState([]);
  const { state } = useContext(OTContext);
  const { connections, speakers, videoWarningIds } = state;

  useEffect(() => subscribeToNotification(handleNewNotification), [
    notifications,
  ]);

  const notificationBuilder = useMemo(
    () => makeNotificationBuilder(formatMessage),
    [formatMessage],
  );

  const hasSlowConnection = useMemo(
    () => videoWarningIds.some(id => speakers.includes(id)),
    [videoWarningIds, speakers],
  );

  const activeSpeakersNames = useMemo(() => {
    const speakersCount = speakers.length;
    if (speakersCount === 0) return '';
    if (speakersCount > 2) {
      return formatMessage(
        { id: 'virtualVisit.room.multipleTalking' },
        { speakersCount },
      );
    }

    return `${speakers
      .map(id => get(connections[id], 'user.name'))
      .join(', ')} ${formatMessage({ id: 'virtualVisit.room.talking' })}`;
  }, [connections, speakers]);

  function handleNewNotification(event) {
    const message = notificationBuilder(event);
    setNotifications([...notifications, { message, id: cuid() }]);
  }

  const removeMessage = useCallback(
    removeId =>
      setNotifications(notifications.filter(({ id }) => id !== removeId)),
    [notifications],
  );

  return (
    <div
      className={cn(styles.root, { [styles.fullScreenMode]: fullScreenView })}
    >
      <TransitionGroup>
        {notifications.map(({ message, id }) => (
          <CSSTransition key={id} timeout={500} classNames="vv-message">
            <Message
              message={message}
              timeout={NOTIFICATION_TIME}
              onClose={() => removeMessage(id)}
              autoClose
            />
          </CSSTransition>
        ))}
        {activeSpeakersNames && (
          <CSSTransition timeout={500} classNames="vv-message">
            <Message
              message={activeSpeakersNames}
              slowConnection={hasSlowConnection}
              formatMessage={formatMessage}
            />
          </CSSTransition>
        )}
      </TransitionGroup>
    </div>
  );
}

VideoNotifications.propTypes = {
  intl: PropTypes.object,
  fullScreenView: PropTypes.bool,
};

export default injectIntl(VideoNotifications);



// WEBPACK FOOTER //
// ./src/components/VirtualVideoRoom/Components/Notifications/index.jsx